import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";

import { trpc } from "@blis/trpc/react";

import { useCommandMenu } from "@/components/CommandMenu";
import { ActiveHoldIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import { CommandEmpty, CommandItem } from "@/components/ui/command";
import { StatusIcon } from "@/components/ui/icons";
import { Text } from "@/components/ui/text";
import type { TAccessionDetail } from "@/lib/types";
import { useRecentlyViewed } from "../../store";

export const accessionSearchPage = {
  id: "search",
  label: "Search",
  searchPlaceholder: "Search accessions...",
  shouldFilter: false,
};

function AccessionCommandItem({ accession }: { accession: TAccessionDetail }) {
  const navigate = useNavigate();
  const { setOpen } = useCommandMenu();

  return (
    <CommandItem
      key={accession.id}
      value={accession.id.toString()}
      onSelect={() => {
        navigate({
          to: "/accessions/$id",
          params: { id: accession.id },
          state: {
            href: { to: "/accessions" },
          },
        });
        setOpen(false);
      }}
      className="items-center justify-between gap-2"
    >
      <div className="flex items-center gap-2">
        <Text muted mono bold size="xs">
          {accession.id}
        </Text>
        <StatusIcon status={accession.status ?? "new"} className="shrink-0" />
        <Text size="1">{accession.caseId}</Text>
        {accession.hasHold && <ActiveHoldIndicator />}
      </div>
      <Badge size="sm">{accession.testType}</Badge>
    </CommandItem>
  );
}

export function AccessionSearchCommand() {
  const { search: originalSearch, setLoading } = useCommandMenu();
  const [search, setSearch] = useState("");
  useDebounce(
    () => {
      setSearch(originalSearch);
    },
    200,
    [originalSearch],
  );
  const [recents] = useRecentlyViewed();
  const { data, isFetching } = trpc.accession.search.useQuery(
    { search, take: 10 },
    {
      enabled: search?.length > 1,
    },
  );
  useEffect(() => {
    setLoading(isFetching);
  }, [isFetching, setLoading]);

  const filteredRecents = recents?.filter((accession) => {
    return search
      ? !data?.find((d) => d.id === accession.id) &&
          (accession.caseId.toLowerCase().includes(search.toLowerCase()) ||
            accession.id.toString().includes(search))
      : true;
  });

  return (
    <>
      {search.length > 2 && data?.length === 0 && (
        <CommandEmpty>No results found.</CommandEmpty>
      )}
      {data?.map((accession) => (
        <AccessionCommandItem key={accession.id} accession={accession} />
      ))}
      {filteredRecents &&
        filteredRecents.length > 0 &&
        filteredRecents.map((accession) => (
          <AccessionCommandItem key={accession.id} accession={accession} />
        ))}
    </>
  );
}
